
import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  UserRound,
  LogOut,
  ArrowLeft,
  FilePlus2,
  FolderPlus,
  ShieldCheck,
  FolderKanban,
  FileText,
  ChevronDown,
} from "lucide-react";
import type { PageLayoutHeaderActions } from "./PageLayout";

interface SecondScreenLayoutProps {
  children: React.ReactNode;
  headerActions?: PageLayoutHeaderActions;
}

const SecondScreenLayout: React.FC<SecondScreenLayoutProps> = ({
  children,
  headerActions,
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  const [showProjectMenu, setShowProjectMenu] = useState(false);
  const [showTenderMenu, setShowTenderMenu] = useState(false);

  const projectNo = localStorage.getItem("projectNo") || "";

  // Project name from navigation state or localStorage.
  const projectName =
    (location.state as { projectName?: string } | null)?.projectName ||
    localStorage.getItem("projectName") ||
    "";

  // Selected tender/contract name.
  const contractName =
    (location.state as { contractName?: string } | null)?.contractName ||
    localStorage.getItem("selectedContractName") ||
    "";

  const userName =
    localStorage.getItem("userName") ||
    localStorage.getItem("username") ||
    "";

  const userType = (
    localStorage.getItem("userType") || ""
  ).toLowerCase();

  const isSuperAdmin = userType === "superadmin";

  const isDashboard = location.pathname === "/dashboard";

  const isContractsPage =
    !!projectNo &&
    location.pathname === `/projects/${projectNo}/contracts`;

  const isSuperAdminPage =
    location.pathname.startsWith("/super-admin");

  const handleLogout = () => {
    [
      "user",
      "userNo",
      "userName",
      "username",
      "userType",
      "authToken",
    ].forEach((key) => localStorage.removeItem(key));

    navigate("/login");
  };

  const handleChangeProject = () => {
    setShowProjectMenu(false);
    setShowTenderMenu(false);
    navigate("/dashboard");
  };

  const handleChangeTender = () => {
    setShowProjectMenu(false);
    setShowTenderMenu(false);

    if (projectNo) {
      navigate(`/projects/${projectNo}/contracts`, {
        state: { projectName },
      });
    } else {
      navigate("/dashboard");
    }
  };

  const inactiveButtonClass =
    "border-b-2 border-transparent text-white/90 " +
    "transition-colors duration-150 " +
    "hover:border-white/50 hover:bg-[#2F7055] hover:text-white";

  const activeButtonClass =
    "border-b-2 border-white bg-[#2F7055] text-white font-medium";

  const buttonBaseClass =
    "inline-flex h-full items-center justify-center gap-1.5 " +
    "whitespace-nowrap px-2.5 text-[12px]";

  const dropdownClass =
    "absolute left-0 top-full z-50 mt-1 min-w-[155px] " +
    "rounded-md border border-gray-200 bg-white py-1 " +
    "text-gray-700 shadow-lg";

  const dropdownItemClass =
    "flex w-full items-center gap-2 px-3 py-2 " +
    "text-left text-[12px] transition-colors " +
    "hover:bg-[#EAF1EC] hover:text-[#245C45]";

  return (
    <div className="flex h-screen flex-col overflow-hidden bg-[#D8E6D3]">
      {/* Header */}
      <header
        className="
          z-20 grid h-[49px] shrink-0 grid-cols-3 items-center
          border-b border-[#1D4936] bg-[#245C45]
          px-3 text-white shadow-sm
        "
      >
        {/* Left: Back, expandable project and tender names */}
        <div className="flex min-w-0 items-center gap-2">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="
              inline-flex shrink-0 items-center gap-1.5 rounded
              px-1.5 py-1 text-[12px] text-white/90
              transition-colors hover:bg-[#2F7055] hover:text-white
            "
          >
            <ArrowLeft size={14} />
            <span>Back</span>
          </button>

          {/* Project name dropdown */}
          <div className="relative min-w-0">
            <button
              type="button"
              aria-expanded={showProjectMenu}
              onClick={() => {
                setShowProjectMenu((previous) => !previous);
                setShowTenderMenu(false);
              }}
              className="
                inline-flex max-w-[175px] items-center gap-1
                rounded px-2 py-1 text-[12px] font-medium
                text-white transition-colors
                hover:bg-[#2F7055]
              "
              title={projectName || "Project"}
            >
              <span className="truncate">
                {projectName || "Project"}
              </span>
              <ChevronDown
                size={12}
                className={`shrink-0 transition-transform ${
                  showProjectMenu ? "rotate-180" : ""
                }`}
              />
            </button>

            {showProjectMenu && (
              <div className={dropdownClass}>
                <button
                  type="button"
                  onClick={handleChangeProject}
                  className={dropdownItemClass}
                >
                  <FolderKanban size={14} />
                  <span>Change Project</span>
                </button>
              </div>
            )}
          </div>

          {/* Tender/contract name dropdown */}
          {contractName && (
            <>
              <span className="shrink-0 text-[12px] text-white/60">
                
              </span>

              <div className="relative min-w-0">
                <button
                  type="button"
                  aria-expanded={showTenderMenu}
                  onClick={() => {
                    setShowTenderMenu((previous) => !previous);
                    setShowProjectMenu(false);
                  }}
                  className="
                    inline-flex max-w-[175px] items-center gap-1
                    rounded px-2 py-1 text-[12px] text-white/90
                    transition-colors hover:bg-[#2F7055]
                    hover:text-white
                  "
                  title={contractName}
                >
                  <span className="truncate">{contractName}</span>
                  <ChevronDown
                    size={12}
                    className={`shrink-0 transition-transform ${
                      showTenderMenu ? "rotate-180" : ""
                    }`}
                  />
                </button>

                {showTenderMenu && (
                  <div className={dropdownClass}>
                    <button
                      type="button"
                      onClick={handleChangeTender}
                      className={dropdownItemClass}
                    >
                      <FileText size={14} />
                      <span>Change Tender</span>
                    </button>
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* Center: Add actions */}
        <div className="flex h-full items-center justify-center gap-1">
          {headerActions?.onAddSection && (
            <button
              type="button"
              onClick={headerActions.onAddSection}
              className={`${buttonBaseClass} ${inactiveButtonClass}`}
            >
              <FolderPlus size={14} />
              <span>Add Section</span>
            </button>
          )}

          {headerActions?.onAddItem && (
            <button
              type="button"
              onClick={headerActions.onAddItem}
              className={`${buttonBaseClass} ${inactiveButtonClass}`}
            >
              <FilePlus2 size={14} />
              <span>Add Item</span>
            </button>
          )}
        </div>

        {/* Right: Navigation, user and logout */}
        <div className="flex h-full min-w-0 items-center justify-end gap-1">
          <button
            type="button"
            onClick={handleChangeProject}
            className={`
              ${buttonBaseClass}
              ${isDashboard ? activeButtonClass : inactiveButtonClass}
            `}
          >
            {/* <FolderKanban size={14} /> */}
            <span></span>
          </button>

          <button
            type="button"
            onClick={handleChangeTender}
            className={`
              ${buttonBaseClass}
              ${isContractsPage ? activeButtonClass : inactiveButtonClass}
            `}
          >
            {/* <FileText size={14} /> */}
            <span></span>
          </button>

          {isSuperAdmin && (
            <button
              type="button"
              onClick={() => navigate("/super-admin")}
              className={`
                ${buttonBaseClass}
                ${
                  isSuperAdminPage
                    ? activeButtonClass
                    : inactiveButtonClass
                }
              `}
            >
              <ShieldCheck size={14} />
              <span>Super Admin</span>
            </button>
          )}

          {userName && (
            <div
              className="
                inline-flex h-full max-w-[130px] shrink-0
                items-center gap-1.5 px-2 text-[12px] text-white
              "
              title={userName}
            >
              <UserRound size={14} className="shrink-0" />
              <span className="truncate">{userName}</span>
            </div>
          )}

          <button
            type="button"
            onClick={handleLogout}
            className={`${buttonBaseClass} ${inactiveButtonClass}`}
          >
            <LogOut size={14} />
            <span>Logout</span>
          </button>
        </div>
      </header>

      {/* Main content: Option 4 muted green background */}
      <main className="min-h-0 flex-1 overflow-auto bg-[#D8E6D3]">
        {children}
      </main>
    </div>
  );
};

export default SecondScreenLayout;
